/**
 * InstallPWABanner — captures the browser's `beforeinstallprompt` event and
 * shows a small dismissible banner inviting the user to install SlideCon to
 * their home screen. On iOS (which doesn't fire beforeinstallprompt) we show
 * a lightweight instructional variant with the "Add to Home Screen" steps —
 * only on mobile Safari and only on the Landing page (?).
 *
 * The banner:
 *   • hides itself if the app is already running in standalone mode,
 *   • is dismissible for 7 days (localStorage flag),
 *   • never shows on auth-callback / stream-output / present routes.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone, Share } from "lucide-react";

const DISMISS_KEY = "slidecon_pwa_install_dismissed";
const DISMISS_DAYS = 7;
const HIDDEN_ROUTES = ["/stream/", "/present/", "/presenter/", "/setlist/", "/onboarding"];

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function isIOSMobileSafari() {
  const ua = window.navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return isIOS && isSafari;
}

export default function InstallPWABanner() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();          // stop the browser mini-infobar
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari doesn't fire beforeinstallprompt — show manual help
    // after a short delay so it isn't in the user's face immediately.
    let iosTimer;
    if (isIOSMobileSafari()) {
      iosTimer = setTimeout(() => setIosHelp(true), 4000);
    }

    // If the user installs successfully, dismiss the banner
    const installed = () => { setVisible(false); setIosHelp(false); };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  // Route filter — hide on presentation/streaming routes
  const shouldHide = HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p));
  if (shouldHide) return null;
  if (!visible && !iosHelp) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    setIosHelp(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") { setVisible(false); }
    setDeferredPrompt(null);
  };

  // ── iOS-only manual instructions variant ─────────────────────────────
  if (iosHelp && !visible) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-40 rounded-xl border border-amber-500/40 bg-white dark:bg-slate-900 shadow-xl p-3 pr-9 flex items-start gap-3"
        role="dialog"
        aria-label="Install SlideCon"
        data-testid="pwa-install-ios-banner"
      >
        <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Smartphone className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1 text-sm">
          <p className="font-semibold text-slate-900 dark:text-slate-100">Add SlideCon to your Home Screen</p>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            Tap <Share className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" /> then <b>Add to Home Screen</b>. It launches like a native app.
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // ── Chromium / Edge / Android — real install prompt ─────────────────
  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-40 rounded-xl border border-amber-500/40 bg-white dark:bg-slate-900 shadow-xl p-3 pr-9 flex items-start gap-3"
      role="dialog"
      aria-label="Install SlideCon"
      data-testid="pwa-install-banner"
    >
      <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
        <Download className="w-4 h-4 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-slate-900 dark:text-slate-100">Install SlideCon</p>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          One tap and SlideCon lives on your home screen — with offline shell + push notifications.
        </p>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            onClick={install}
            className="h-8 bg-amber-500 hover:bg-amber-600 text-slate-900 gap-1.5"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Install
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            className="h-8 text-slate-500"
            data-testid="pwa-install-later"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        data-testid="pwa-install-dismiss-x"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
